import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { categoryService, productService, businessService, ingredientService } from '../services/api';
import { useProductEvents } from '../hooks/useSocket';
import { useMenuTimeTracking } from '../hooks/useMenuTimeTracking';
import SharedFooter from '../components/SharedFooter';
import { useCart } from '../contexts/CartContext';

// URL base per le risorse statiche via API (proxy-safe)
const API_BASE_URL = (typeof window !== 'undefined' ? `${window.location.origin}/api` : ((process.env.REACT_APP_API_URL as string | undefined) || '/api'));

interface Product {
  id: number;
  name: string;
  name_en?: string;
  description?: string;
  description_en?: string;
  price: number;
  price_unit?: string;
  image_path?: string;
  is_active: number;
  category_id: number;
  category_name: string;
  allergens: Array<{
    id: number;
    name: string;
    name_en?: string;
  }>;
  ingredients: Array<{
    id: number;
    name: string;
    name_en?: string;
  }>;
}

interface BusinessInfo {
  id: number;
  name: string;
  description: string;
  address: string;
  city: string;
  postal_code: string;
  phone: string;
  email: string;
  website: string;
  logo_url?: string;
  asporto_enabled?: boolean | number;
  whatsapp?: string;
}

interface Allergen {
  id: number;
  name: string;
  name_en?: string;
  icon?: string;
}

interface Ingredient {
  id: number;
  name: string;
  name_en?: string;
  icon?: string;
}

interface Category {
  id: number;
  name: string;
  name_en?: string;
  description?: string;
  description_en?: string;
  children?: Category[];
  parent_id?: number;
  sort_order?: number;
}

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f5f7fa;
  padding: 16px;
  padding-bottom: 90px;

  @media (max-width: 480px) {
    padding: 10px;
    padding-bottom: 80px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
`;

const StickyBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 1000;
  background: #f5f7fa;
  margin-left: -16px;
  margin-right: -16px;
  padding: 8px 16px 12px;
  border-bottom: none;
  box-shadow: none;

  @media (max-width: 480px) {
    margin-left: -10px;
    margin-right: -10px;
    padding-left: 10px;
    padding-right: 10px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const BackButton = styled.button`
  background: rgba(204, 157, 109, 0.1);
  border: 1px solid rgba(204, 157, 109, 0.3);
  color: #cc9d6d;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: rgba(204, 157, 109, 0.2);
    transform: translateY(-1px);
  }
`;

const CategoryTitle = styled.h1`
  color: #cc9d6d;
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const CategoryDescription = styled.p`
  color: #666;
  font-size: 1rem;
  margin: 4px 0 0 0;
  font-weight: 400;
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  max-width: 1100px;
  margin: 0 auto;
  margin-top: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 10px;
    margin-top: 10px;
  }
`;

const ProductCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 14px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  }

  @media (max-width: 640px) {
    padding: 12px;
    border-radius: 12px;
  }
`;

const ProductImage = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'hasImage',
})<{ hasImage: boolean }>`
  width: 100%;
  height: 140px;
  border-radius: 12px;
  margin-bottom: 12px;
  background: ${props => props.hasImage ? 'none' : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 12px;
  }

  @media (max-width: 640px) {
    height: 110px;
    margin-bottom: 8px;
  }
`;

const ProductName = styled.h3`
  color: #2d3748;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 6px 0;
  line-height: 1.3;

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const PriceBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(204, 157, 109, 0.95);
  color: #fff;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 700;
  box-shadow: 0 4px 10px rgba(0,0,0,0.15);
`;

const ProductDescription = styled.p`
  color: #4a5568;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 10px;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 15px;
`;

const SubcategoryTags = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;
  margin: 8px 0 4px 0;
  overflow-x: auto;
  padding-bottom: 6px;
  scrollbar-width: thin;
  scrollbar-color: rgba(204, 157, 109, 0.5) transparent;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(204, 157, 109, 0.6);
    border-radius: 999px;
  }
`;

const IngredientsSection = styled.div`
  margin-bottom: 8px;
`;

const AllergensSection = styled.div`
  margin-bottom: 8px;
`;

const SectionRow = styled.div`
  display: block;
`;

const SectionLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #4a5568;
  margin-right: 6px;
`;

const ItemsList = styled.div`
  color: #6b7280;
  display: inline;
  font-size: 0.85rem;
  line-height: 1.5;
  margin: 0;
  padding: 0;
  word-break: break-word;
`;

// Chip sottocategoria con colore coerente
// Chip sottocategoria con stato attivo per evidenziare il filtro
const SubcategoryChip = styled.button<{ $active?: boolean }>`
  background: rgba(204, 157, 109, 0.12);
  color: #b8906b;
  padding: 0 16px;
  height: 38px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  border: 1px solid rgba(204, 157, 109, 0.4);
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(204, 157, 109, 0.2);
  }

  ${props => props.$active ? `
    background: rgba(204, 157, 109, 0.25);
    color: #9c6f4a;
    border-color: rgba(204, 157, 109, 0.8);
  ` : ''}
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
  color: #666;
  font-size: 1.2rem;
`;

const EmptyState = styled.div`
  text-align: center;
  color: #666;
  font-size: 1.2rem;
  margin-top: 50px;
`;

const FixedNavigation = styled.div`
  position: fixed;
  bottom: 90px; /* posizionato sopra il footer fisso */
  right: 20px;
  z-index: 1100;
`;

const FloatingBackButton = styled.button`
  background: #cc9d6d;
  color: white;
  border: none;
  padding: 10px 16px; /* più piccolo */
  border-radius: 40px;
  cursor: pointer;
  font-size: 14px; /* più piccolo */
  font-weight: 600;
  box-shadow: 0 4px 20px rgba(204, 157, 109, 0.3);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: #b8906b;
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(204, 157, 109, 0.4);
  }
`;

const AddButton = styled.button`
  width: 100%;
  margin-top: 10px;
  padding: 9px 16px;
  background: #cc9d6d;
  color: white;
  border: none;
  border-radius: 40px;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 8px rgba(204, 157, 109, 0.3);
  transition: all 0.2s ease;
  &:hover {
    background: #b8906b;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(204, 157, 109, 0.4);
  }
`;

const QtyControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  background: rgba(204, 157, 109, 0.08);
  border: 1px solid rgba(204, 157, 109, 0.4);
  border-radius: 40px;
  padding: 4px 8px;
`;

const QtyBtn = styled.button`
  background: none;
  border: none;
  color: #cc9d6d;
  font-size: 1.2rem;
  font-weight: 700;
  cursor: pointer;
  padding: 2px 10px;
  &:hover { color: #b8906b; }
`;

const QtyValue = styled.span`
  font-weight: 700;
  color: #b8906b;
  font-size: 1rem;
`;

const CustomizeButton = styled.button`
  width: 100%;
  margin-top: 8px;
  padding: 8px 16px;
  background: white;
  color: #cc9d6d;
  border: 1.5px solid rgba(204, 157, 109, 0.5);
  border-radius: 40px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { background: rgba(204, 157, 109, 0.08); border-color: #cc9d6d; }
`;

// Modale personalizza piatto (stile McDonald's)
const CzOverlay = styled.div`
  position: fixed; inset: 0; z-index: 1300;
  background: rgba(0,0,0,0.5);
  display: flex; align-items: flex-end; justify-content: center;
`;
const CzPanel = styled.div`
  background: white; border-radius: 20px 20px 0 0; width: 100%; max-width: 600px;
  max-height: 88vh; display: flex; flex-direction: column;
`;
const CzHeader = styled.div`
  padding: 18px 20px 12px; border-bottom: 1px solid #f3f4f6;
  display: flex; align-items: flex-start; justify-content: space-between; gap: 10px;
`;
const CzTitle = styled.div`font-size: 17px; font-weight: 700; color: #111827;`;
const CzPrice = styled.div`font-size: 13px; color: #cc9d6d; font-weight: 600; margin-top: 2px;`;
const CzClose = styled.button`
  background: #f3f4f6; border: none; border-radius: 50%; width: 32px; height: 32px;
  font-size: 16px; cursor: pointer; flex-shrink: 0;
`;
const CzBody = styled.div`flex: 1; overflow-y: auto; padding: 14px 20px;`;
const CzLabel = styled.div`font-size: 11px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 1px; margin: 12px 0 8px; &:first-child { margin-top: 0; }`;
const CzChips = styled.div`display: flex; flex-wrap: wrap; gap: 8px;`;
const CzChip = styled.button<{ $removed?: boolean }>`
  padding: 7px 14px; border-radius: 20px; font-size: 13px; font-weight: 500; cursor: pointer;
  border: 1.5px solid ${p => p.$removed ? '#fca5a5' : '#d1fae5'};
  background: ${p => p.$removed ? '#fef2f2' : '#f0fdf4'};
  color: ${p => p.$removed ? '#dc2626' : '#16a34a'};
  text-decoration: ${p => p.$removed ? 'line-through' : 'none'};
`;
const CzSearch = styled.input`
  width: 100%; padding: 9px 12px; border: 1.5px solid #e5e7eb; border-radius: 8px;
  font-size: 14px; box-sizing: border-box;
  &:focus { outline: none; border-color: #818cf8; }
`;
const CzResults = styled.div`
  border: 1px solid #e5e7eb; border-radius: 8px; margin-top: 4px;
  max-height: 150px; overflow-y: auto;
`;
const CzResult = styled.button`
  width: 100%; text-align: left; padding: 9px 12px; border: none; background: white;
  font-size: 14px; color: #1f2937; cursor: pointer; border-bottom: 1px solid #f9fafb;
  &:last-child { border-bottom: none; }
  &:hover { background: #eef2ff; color: #4f46e5; }
`;
const CzAddedChips = styled.div`display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px;`;
const CzAddedChip = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 5px 10px; border-radius: 20px;
  background: #eef2ff; color: #4f46e5; border: 1px solid #c7d2fe; font-size: 13px; font-weight: 500;
`;
const CzChipX = styled.button`background: none; border: none; color: #818cf8; cursor: pointer; font-size: 14px; padding: 0; line-height: 1;`;
const CzNote = styled.textarea`
  width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px;
  font-size: 14px; box-sizing: border-box; resize: none; font-family: inherit;
  &:focus { outline: none; border-color: #cc9d6d; }
`;
const CzFooter = styled.div`padding: 12px 20px 16px; border-top: 1px solid #f3f4f6; display: flex; align-items: center; gap: 12px;`;
const CzQty = styled.div`display: flex; align-items: center; gap: 10px;`;
const CzQtyBtn = styled.button`
  width: 34px; height: 34px; border-radius: 50%; border: 1px solid #d1d5db;
  background: white; font-size: 18px; font-weight: 700; cursor: pointer; color: #374151;
`;
const CzQtyNum = styled.div`font-size: 17px; font-weight: 800; min-width: 24px; text-align: center;`;
const CzAddBtn = styled.button`
  flex: 1; padding: 13px; border-radius: 12px; border: none;
  background: #cc9d6d; color: white; font-size: 15px; font-weight: 700; cursor: pointer;
  &:hover { background: #b8906b; }
`;

const FloatingCartBtn = styled.button`
  position: fixed;
  bottom: 92px;
  right: 20px;
  z-index: 1100;
  background: #cc9d6d;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 40px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 700;
  box-shadow: 0 4px 20px rgba(204, 157, 109, 0.4);
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: 0.3px;
  transition: all 0.25s ease;
  &:hover { background: #b8906b; transform: translateY(-2px); box-shadow: 0 6px 25px rgba(204, 157, 109, 0.5); }
`;

const AllergenToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  color: #4a5568;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
`;

const AllergenArrow = styled.span<{ $open: boolean }>`
  font-size: 0.7rem;
  color: #9ca3af;
  transition: transform 0.2s ease;
  transform: rotate(${p => p.$open ? 180 : 0}deg);
`;

// Allergeni a scomparsa: mostra solo l'etichetta, si aprono al click
const AllergensCollapsible: React.FC<{ names: string[]; language: 'it' | 'en' }> = ({ names, language }) => {
  const [open, setOpen] = useState(false);
  return (
    <AllergensSection>
      <AllergenToggleBtn type="button" onClick={() => setOpen(o => !o)}>
        <span>⚠️</span>
        <span>{language === 'en' ? 'Allergens' : 'Allergeni'}</span>
        <AllergenArrow $open={open}>▾</AllergenArrow>
      </AllergenToggleBtn>
      {open && (
        <ItemsList style={{ display: 'block', marginTop: 6 }}>
          {names.join(', ')}
        </ItemsList>
      )}
    </AllergensSection>
  );
};

const CategoryProducts: React.FC = () => {
  const params = useParams();
  // Support both 'id' (standard) and 'categoryId' (legacy/specific) parameter names
  const categoryId = params.id || params.categoryId;
  const navigate = useNavigate();
  const location = useLocation();
  // Solo nel menu pubblico (la stessa pagina è usata anche nell'admin)
  useMenuTimeTracking(location.pathname.startsWith('/menu'));

  const handleBack = () => {
    if (location.pathname.startsWith('/menu')) {
      navigate('/menu');
    } else {
      navigate('/categories');
    }
  };
  
  const [products, setProducts] = useState<Product[]>([]);
  const [category, setCategory] = useState<Category | null>(null);
  const [subcategories, setSubcategories] = useState<Category[]>([]);
  const [publicCategories, setPublicCategories] = useState<Category[]>([]);
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSubcategoryId, setActiveSubcategoryId] = useState<number | null>(null);
  const [language] = useState<'it' | 'en'>(
    (typeof window !== 'undefined' && localStorage.getItem('menu-language') === 'en') ? 'en' : 'it'
  );
  const { addItem, addCustomItem, items: cart, updateQty: updateCartQty, cartCount, asportoEnabled, asportoMode, cartOpen, setCartOpen, whatsappNumber, businessName, setAsportoEnabled, setWhatsappNumber, setBusinessName, tableUuid } = useCart();
  const orderingEnabled = asportoMode || !!tableUuid;

  // Personalizzazione piatto (stile McDonald's)
  const [czProduct, setCzProduct] = useState<Product | null>(null);
  const [czRemoved, setCzRemoved] = useState<Set<number>>(new Set());
  const [czExtras, setCzExtras] = useState<Array<{ id: number; name: string }>>([]);
  const [czSearch, setCzSearch] = useState('');
  const [czNote, setCzNote] = useState('');
  const [czQty, setCzQty] = useState(1);
  const [allIngredients, setAllIngredients] = useState<Array<{ id: number; name: string }>>([]);

  useEffect(() => {
    ingredientService.getAll()
      .then((r: any) => setAllIngredients(r?.data?.ingredients || r?.ingredients || []))
      .catch(() => {});
  }, []);

  const openCustomize = (product: Product) => {
    setCzProduct(product);
    setCzRemoved(new Set());
    setCzExtras([]);
    setCzSearch('');
    setCzNote('');
    setCzQty(1);
  };

  const czToggleRemove = (id: number) => {
    setCzRemoved(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const buildCzNote = (): string => {
    const parts: string[] = [];
    if (czProduct?.ingredients && czRemoved.size > 0) {
      const names = czProduct.ingredients.filter(i => czRemoved.has(i.id)).map(i => i.name).join(', ');
      if (names) parts.push(`Senza: ${names}`);
    }
    if (czExtras.length > 0) parts.push(`Aggiungi: ${czExtras.map(i => i.name).join(', ')}`);
    if (czNote.trim()) parts.push(czNote.trim());
    return parts.join(' · ');
  };

  const confirmCustomize = () => {
    if (!czProduct) return;
    addCustomItem({ id: czProduct.id, name: czProduct.name, price: czProduct.price }, buildCzNote(), czQty);
    setCzProduct(null);
  };

  // Funzione per ricaricare i prodotti
  const reloadProducts = useCallback(async () => {
    if (!categoryId) return;
    try {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Ricaricamento prodotti (contesto padre) in corso...');
      }
      // Ottieni categoria corrente e mappa pubblica per calcolare il padre
      const [categoryResponse, parentsResponse] = await Promise.all([
        categoryService.getById(parseInt(categoryId)),
        categoryService.getPublic()
      ]);
      let publicParents: Category[] = [];
      if (parentsResponse?.success && parentsResponse.data?.categories) {
        publicParents = parentsResponse.data.categories || [];
      }
      const currentCat: Category | null = categoryResponse?.success ? categoryResponse.data.category : null;
      if (!currentCat) return;
      const directParent = publicParents.find((c) => (c.children || []).some((child) => child.id === currentCat.id));
      const isTopLevel = publicParents.some((c) => c.id === currentCat.id);
      const contextParent = directParent || (isTopLevel ? currentCat : currentCat);

      // Carica i prodotti del padre per permettere il filtro locale tra sottocategorie
      try {
        const productsResponse = await productService.getByCategory(contextParent.id);
        if (productsResponse.success) {
          setProducts(productsResponse.data.products);
          // Manteniamo la categoria in base al route corrente
          setCategory(currentCat);
          if (process.env.NODE_ENV !== 'production') {
            console.log('Prodotti ricaricati con successo (contesto padre)');
          }
        } else {
            console.error('Errore nel caricamento dei prodotti: response success false', productsResponse);
            setError('Impossibile caricare i prodotti');
        }
      } catch (prodErr) {
        console.error('Errore chiamata API prodotti:', prodErr);
        setError('Errore di connessione al server');
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Errore nel ricaricamento dei prodotti (contesto padre):', err);
      }
      setError('Errore generale nel caricamento');
    } finally {
        setLoading(false);
    }
  }, [categoryId]);

  // Gestione eventi Socket.IO per aggiornamenti in tempo reale
  useProductEvents({
    onProductAdded: (data) => {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Prodotto aggiunto, ricaricamento prodotti...', data);
      }
      reloadProducts();
    },
    onProductUpdated: (data) => {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Prodotto aggiornato, ricaricamento prodotti...', data);
      }
      reloadProducts();
    },
    onProductDeleted: (data) => {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Prodotto eliminato, ricaricamento prodotti...', data);
      }
      reloadProducts();
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!categoryId) return;
      
      try {
        setLoading(true);
        
        // Carica info categoria (con figli) e business; poi categorie pubbliche per contesto
        const [categoryResponse, businessResponse] = await Promise.all([
          categoryService.getById(parseInt(categoryId)),
          businessService.get()
        ]);
        const parentsResponse = await categoryService.getPublic();

        // Prepara categorie pubbliche e calcola il contesto (categoria padre) per mostrare SOLO i tag associati
        let publicParents: Category[] = [];
        if (parentsResponse?.success && parentsResponse.data?.categories) {
          publicParents = parentsResponse.data.categories || [];
          setPublicCategories(publicParents);
        }

        // Imposta sottocategorie dal contesto di categoria (padre): restano visibili anche navigando tra sottocategorie
        if (categoryResponse?.success && categoryResponse.data?.category) {
          const currentCat: Category = categoryResponse.data.category;

          // Trova la categoria padre nel set pubblico: se currentCat è figlio, usa il suo padre; altrimenti usa currentCat
          const findParent = (): Category | null => {
            // Diretti: currentCat come figlio di un top-level
            const directParent = publicParents.find((c) => (c.children || []).some((child) => child.id === currentCat.id));
            if (directParent) return directParent;
            // Se currentCat è top-level, il contesto è lui stesso
            const isTopLevel = publicParents.some((c) => c.id === currentCat.id);
            if (isTopLevel) return currentCat;
            return null;
          };

          const contextParent = findParent() || currentCat;
          // Se siamo entrati da una sottocategoria, selezioniamo quella come filtro attivo
          const fromSubcategory = !!publicParents.find((c) => (c.children || []).some((child) => child.id === currentCat.id));
          setActiveSubcategoryId(fromSubcategory ? currentCat.id : null);

          // Carica i prodotti del padre per avere tutte le sottocategorie filtrabili
          const productsResponse = await productService.getByCategory(contextParent.id);
          if (productsResponse.success) {
            setProducts(productsResponse.data.products);
            setCategory(currentCat); // Manteniamo il titolo coerente con la categoria corrente
          } else {
            setError('Errore nel caricamento dei prodotti');
          }

          const productCategoryIds = new Set(
            (productsResponse?.success ? productsResponse.data.products : []).map((p: Product) => p.category_id)
          );
          let contextChildren = (contextParent.children || []).filter((sub: Category) => productCategoryIds.has(sub.id));
          // Ordina per sort_order con fallback al nome
          contextChildren.sort((a, b) => {
            const ao = a.sort_order ?? 0;
            const bo = b.sort_order ?? 0;
            if (ao !== bo) return ao - bo;
            return (a.name || '').localeCompare(b.name || '');
          });
          setSubcategories(contextChildren);
        }

        // Il businessService restituisce direttamente i dati
        if (businessResponse) {
          setBusinessInfo(businessResponse);
          // Sync CartContext (utile se si accede direttamente a questa pagina senza passare per PublicMenu)
          setAsportoEnabled(Boolean(businessResponse.asporto_enabled));
          setWhatsappNumber(businessResponse.whatsapp || '');
          setBusinessName(businessResponse.name || '');
        }
        
      } catch (err) {
        console.error('Errore nel caricamento dei dati:', err);
        setError('Errore nel caricamento dei dati');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [categoryId]);

  if (loading) {
    return (
      <PageContainer>
        <LoadingState>{language === 'en' ? 'Loading products...' : 'Caricamento prodotti...'}</LoadingState>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Header>
          <BackButton onClick={handleBack}>
            {language === 'en' ? '← Back to Menu' : '← Torna al Menu'}
          </BackButton>
        </Header>
        <EmptyState>{error}</EmptyState>
      </PageContainer>
    );
  }

  const displayCategory = activeSubcategoryId
    ? subcategories.find((sub) => sub.id === activeSubcategoryId) || category
    : category;

  return (
    <PageContainer>
      <StickyBar>
        <Header>
          <HeaderLeft>
            <BackButton onClick={handleBack}>
              {language === 'en' ? '← Back to Menu' : '← Torna al Menu'}
            </BackButton>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <CategoryTitle>{language === 'en' ? (displayCategory?.name_en || displayCategory?.name) : (displayCategory?.name || 'Categoria')}</CategoryTitle>
            </div>
          </HeaderLeft>
        </Header>

        {/* Tags sottocategorie (filtri rapidi) */}
        {subcategories && subcategories.length > 0 && (
          <SubcategoryTags>
            {activeSubcategoryId === null && (
              <SubcategoryChip
                $active={true}
                onClick={() => setActiveSubcategoryId(null)}
              >
                {language === 'en' ? 'All' : 'Tutti'}
              </SubcategoryChip>
            )}
            {subcategories.map((sub) => (
              <SubcategoryChip
                key={sub.id}
                $active={activeSubcategoryId === sub.id}
                onClick={() => setActiveSubcategoryId(prev => prev === sub.id ? null : sub.id)}
              >
                {language === 'en' ? (sub.name_en || sub.name) : sub.name}
              </SubcategoryChip>
            ))}
          </SubcategoryTags>
        )}
        {(language === 'en' ? displayCategory?.description_en : displayCategory?.description) && (
          <CategoryDescription>
            {language === 'en' ? displayCategory?.description_en : displayCategory?.description}
          </CategoryDescription>
        )}
      </StickyBar>

      {products.length === 0 ? (
        <EmptyState>{language === 'en' ? 'No products available in this category.' : 'Nessun prodotto disponibile in questa categoria.'}</EmptyState>
      ) : (
        <ProductsGrid>
          {(activeSubcategoryId ? products.filter(p => p.category_id === activeSubcategoryId) : products).map((product) => (
            <ProductCard key={product.id}>
              <ProductImage hasImage={true}>
                {product.image_path ? (
                  <img 
                    src={`${API_BASE_URL}${product.image_path}`}
                    alt={product.name}
                    onError={(e) => {
                      // Se l'immagine del prodotto non si carica, mostra l'immagine di default
                      e.currentTarget.src = '/nofoto.jpg';
                    }}
                  />
                ) : (
                  <img 
                    src="/nofoto.jpg"
                    alt="Immagine non disponibile"
                  />
                )}
                {/* Prezzo come badge nell'immagine con unità */}
                <PriceBadge>
                  € {product.price.toFixed(2)}
                  {product.price_unit ? ` / ${product.price_unit}` : ''}
                </PriceBadge>
              </ProductImage>

              <ProductName>{language === 'en' ? (product.name_en || product.name) : product.name}</ProductName>
              
              {/* Ingredienti */}
              {product.ingredients && product.ingredients.length > 0 && (
                <IngredientsSection>
                  <SectionRow>
                    <SectionLabel>
                      <span>🥄</span>
                      <span>{language === 'en' ? 'Ingredients:' : 'Ingredienti:'}</span>
                    </SectionLabel>
                    <ItemsList>
                      {product.ingredients
                        .map((ingredient) => (
                          language === 'en'
                            ? (ingredient?.name_en || ingredient?.name || '')
                            : (ingredient?.name || '')
                        ))
                        .filter(Boolean)
                        .sort((a, b) => (a || '').localeCompare((b || ''), language === 'en' ? 'en' : 'it', { sensitivity: 'base' }))
                        .join(', ')}
                    </ItemsList>
                  </SectionRow>
                </IngredientsSection>
              )}
              
              {/* Allergeni (a scomparsa) */}
              {product.allergens && product.allergens.length > 0 && (
                <AllergensCollapsible
                  language={language}
                  names={product.allergens
                    .map((allergen) => (
                      language === 'en'
                        ? (allergen?.name_en || allergen?.name || '')
                        : (allergen?.name || '')
                    ))
                    .filter(Boolean)
                    .sort((a, b) => (a || '').localeCompare((b || ''), language === 'en' ? 'en' : 'it', { sensitivity: 'base' }))}
                />
              )}

              {(language === 'en' ? (product.description_en || product.description) : product.description) && (
                <ProductDescription>
                  {language === 'en' ? (product.description_en || product.description) : product.description}
                </ProductDescription>
              )}

              {/* Bottoni aggiungi / personalizza (asporto o ordine al tavolo) */}
              {orderingEnabled && (() => {
                const cartItem = cart.find(i => i.id === product.id && !i.notes);
                return (
                  <>
                    {cartItem ? (
                      <QtyControls>
                        <QtyBtn onClick={() => updateCartQty(cartItem.lineId, cartItem.quantity - 1)}>−</QtyBtn>
                        <QtyValue>{cartItem.quantity}</QtyValue>
                        <QtyBtn onClick={() => updateCartQty(cartItem.lineId, cartItem.quantity + 1)}>+</QtyBtn>
                      </QtyControls>
                    ) : (
                      <AddButton onClick={() => addItem({ id: product.id, name: product.name, price: product.price })}>
                        🛒 {language === 'en' ? 'Add' : 'Aggiungi'}
                      </AddButton>
                    )}
                    <CustomizeButton onClick={() => openCustomize(product)}>
                      ⚙️ {language === 'en' ? 'Customize' : 'Personalizza'}
                    </CustomizeButton>
                  </>
                );
              })()}
            </ProductCard>
          ))}
        </ProductsGrid>
      )}
      
      {/* Footer condiviso (include bottone asporto e pannello carrello) */}
      <SharedFooter />

      {/* Modale personalizza piatto */}
      {czProduct && (() => {
        const dishIds = new Set((czProduct.ingredients || []).map(i => i.id));
        const addedIds = new Set(czExtras.map(i => i.id));
        const results = czSearch.trim()
          ? allIngredients.filter(i => !dishIds.has(i.id) && !addedIds.has(i.id) && i.name.toLowerCase().includes(czSearch.toLowerCase())).slice(0, 8)
          : [];
        return (
          <CzOverlay onClick={() => setCzProduct(null)}>
            <CzPanel onClick={e => e.stopPropagation()}>
              <CzHeader>
                <div>
                  <CzTitle>{language === 'en' ? (czProduct.name_en || czProduct.name) : czProduct.name}</CzTitle>
                  <CzPrice>€ {Number(czProduct.price).toFixed(2)}{czProduct.price_unit ? ` / ${czProduct.price_unit}` : ''}</CzPrice>
                </div>
                <CzClose onClick={() => setCzProduct(null)}>✕</CzClose>
              </CzHeader>
              <CzBody>
                {czProduct.ingredients && czProduct.ingredients.length > 0 && (
                  <>
                    <CzLabel>{language === 'en' ? 'Ingredients — tap to remove' : 'Ingredienti — tocca per togliere'}</CzLabel>
                    <CzChips>
                      {czProduct.ingredients.map(ing => (
                        <CzChip key={ing.id} $removed={czRemoved.has(ing.id)} onClick={() => czToggleRemove(ing.id)}>
                          {czRemoved.has(ing.id) ? '✕ ' : '✓ '}{language === 'en' ? (ing.name_en || ing.name) : ing.name}
                        </CzChip>
                      ))}
                    </CzChips>
                  </>
                )}

                <CzLabel>{language === 'en' ? 'Add extra' : 'Aggiungi'}</CzLabel>
                <CzSearch
                  placeholder={language === 'en' ? 'Search ingredient…' : 'Cerca ingrediente da aggiungere…'}
                  value={czSearch}
                  onChange={e => setCzSearch(e.target.value)}
                />
                {results.length > 0 && (
                  <CzResults>
                    {results.map(ing => (
                      <CzResult key={ing.id} type="button" onClick={() => { setCzExtras(prev => [...prev, ing]); setCzSearch(''); }}>
                        + {ing.name}
                      </CzResult>
                    ))}
                  </CzResults>
                )}
                {czExtras.length > 0 && (
                  <CzAddedChips>
                    {czExtras.map(ing => (
                      <CzAddedChip key={ing.id}>
                        {ing.name}
                        <CzChipX type="button" onClick={() => setCzExtras(prev => prev.filter(i => i.id !== ing.id))}>✕</CzChipX>
                      </CzAddedChip>
                    ))}
                  </CzAddedChips>
                )}

                <CzLabel>{language === 'en' ? 'Notes' : 'Nota'}</CzLabel>
                <CzNote
                  rows={2}
                  placeholder={language === 'en' ? 'E.g. well done, no salt…' : 'Es. ben cotto, poco sale…'}
                  value={czNote}
                  onChange={e => setCzNote(e.target.value)}
                />
              </CzBody>
              <CzFooter>
                <CzQty>
                  <CzQtyBtn onClick={() => setCzQty(q => Math.max(1, q - 1))}>−</CzQtyBtn>
                  <CzQtyNum>{czQty}</CzQtyNum>
                  <CzQtyBtn onClick={() => setCzQty(q => q + 1)}>+</CzQtyBtn>
                </CzQty>
                <CzAddBtn onClick={confirmCustomize}>
                  {language === 'en' ? 'Add to order' : 'Aggiungi all\'ordine'}
                </CzAddBtn>
              </CzFooter>
            </CzPanel>
          </CzOverlay>
        );
      })()}
    </PageContainer>
  );
};

export default CategoryProducts;
